import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { RequestHistoryTable } from "../components/requests/RequestHistoryTable";
import { RequestTileLoading, RequestTiles } from "../components/requests/RequestTiles";
import { isOpenRequest } from "../components/requests/requestHistoryFormat";
import { RequestViewSwitch } from "../components/requests/RequestViewSwitch";
import { readStoredRequestView, storeRequestView, type RequestView } from "../components/requests/requestView";
import { ApproverRequests } from "../components/requests/ApproverRequests";
import { JoinTeamPanel } from "../components/requests/JoinTeamPanel";
import { EmptyState, ErrorState, PermissionDeniedState } from "../components/states";
import { useSession } from "../session/SessionContext";
import { useNarrow } from "../hooks/useNarrow";
import { fetchMyRequestHistory } from "../api/myRequests";
import type { PersonRequestHistoryItem } from "../api/requestsAdmin";
import styles from "./MyRequestsPage.module.css";
import { unchangedOr, usePolling } from "../hooks/usePolling";

type PageState =
  | { kind: "loading" }
  | { kind: "loaded"; items: PersonRequestHistoryItem[] }
  | { kind: "denied" }
  | { kind: "error"; message: string };

/** The permission that makes an approver: the requests assigned to them are decided from this page. */
const ApprovePermission = "request.approve";
const JoinTeamPermission = "request.join-team";

/** How many finished requests a phone lists under the pending ones: enough to confirm a recent request was dealt with. */
const RecentCompletedCount = 5;

/**
 * The "My Requests" screen: every request the signed-in engineer has raised for access or a change to their
 * allocation. Open requests (pending, or approved and waiting for an administrator to set something up) are shown by default; completed ones, which include withdrawn requests,
 * sit behind the "Show Completed Requests" toggle. The requests are shown as tiles, one under another, or as a table whose
 * rows expand to show their details; the person picks, and the choice is remembered on this browser.
 *
 * On a phone the page shows the pending requests as tiles, and under them the few most recently finished, so someone
 * checking on the move can see what is still waiting and confirm that a recent request was dealt with. The full history
 * is a table that needs a wider screen.
 *
 * An approver (`request.approve`) also sees, above their own requests, a "Waiting for my decision" section: the open requests in their own queue, currently
 * assigned to them, with the details and the buttons to decide them, as the Administration Requests screen has. The page then
 * scrolls as a whole, and the table of their own requests keeps a usable height.
 */
export function MyRequestsPage(): React.JSX.Element {
  const { t } = useTranslation();
  const { snapshot } = useSession();
  const [state, setState] = useState<PageState>({ kind: "loading" });
  const [hasAssigned, setHasAssigned] = useState(false);
  // null until the engineer touches the toggle; until then the default follows the data (see below).
  const [completedChoice, setCompletedChoice] = useState<boolean | null>(null);
  const signedIn = snapshot.status === "signed-in";
  const isApprover = snapshot.status === "signed-in" && snapshot.permissions.includes(ApprovePermission);
  const canJoinTeam = snapshot.status === "signed-in" && snapshot.permissions.includes(JoinTeamPermission);
  const narrow = useNarrow();
  const [view, setViewState] = useState<RequestView>(readStoredRequestView);
  const setView = (next: RequestView): void => {
    setViewState(next);
    storeRequestView(next);
  };

  // A quiet load refreshes the requests in place, without the loading layout, and keeps them if it fails.
  const load = useCallback(async (quiet = false) => {
    if (!quiet) {
      setState({ kind: "loading" });
    }
    const result = await fetchMyRequestHistory();
    if (quiet && result.kind !== "ok") {
      return;
    }
    switch (result.kind) {
      case "ok": {
        const next: PageState = { kind: "loaded", items: result.response };
        setState((previous) => unchangedOr(previous, next));
        return;
      }
      case "denied":
        setState({ kind: "denied" });
        return;
      case "conflict":
      case "error":
        setState({ kind: "error", message: result.message });
    }
  }, []);

  useEffect(() => {
    if (signedIn) {
      void load();
    }
  }, [load, signedIn]);

  usePolling(() => load(true), signedIn && state.kind === "loaded");

  if (!signedIn) {
    return <PermissionDeniedState title={t("myRequests.denied.title", "Sign in to see your requests")} description={t("myRequests.denied.description", "Your requests appear here once you have signed in.")} />;
  }

  const items = state.kind === "loaded" ? state.items : [];
  // Completed requests are off while anything is pending, and on when nothing is, so the table is never blank by default.
  const hasPending = items.some((item) => isOpenRequest(item.status));
  // Until the requests arrive, the switch shows only what the person chose; the default follows the data once there is some.
  const showCompleted = completedChoice ?? (state.kind === "loaded" ? !hasPending : false);
  const loadingLabel = t("myRequests.loading", "Loading your requests…");
  const pending = items.filter((item) => isOpenRequest(item.status));
  const completed = items
    .filter((item) => !isOpenRequest(item.status))
    .sort((a, b) => (b.decidedAt ?? b.createdAt).localeCompare(a.decidedAt ?? a.createdAt));
  const visible = showCompleted ? items : pending;
  const noPending = (
    <EmptyState
      title={t("myRequests.noPendingTitle", "No pending requests")}
      description={
        // The hint to show finished requests is only for when they are hidden; otherwise they are already below.
        !narrow && !showCompleted
          ? t("myRequests.noCurrentDescription", "Turn on Show Completed Requests to see your finished requests.")
          : t("myRequests.noPendingNarrowDescription", "Nothing is waiting for a decision.")
      }
    />
  );

  // The open requests, then the finished ones given, each list named for screen readers; the tiles scroll inside the page.
  const renderTiles = (finished: PersonRequestHistoryItem[]): React.JSX.Element => (
    <div className={styles.tiles}>
      {pending.length === 0 ? (
        noPending
      ) : (
        <section aria-labelledby="pending-requests-heading">
          <h2 id="pending-requests-heading" className="visually-hidden">{t("myRequests.pendingTitle", "Pending")}</h2>
          <RequestTiles items={pending} label={t("myRequests.pendingTitle", "Pending")} onCancelled={() => void load(true)} />
        </section>
      )}
      {finished.length > 0 && (
        <section aria-labelledby="recent-requests-heading">
          <h2 id="recent-requests-heading" className="visually-hidden">{t("myRequests.recentTitle", "Recently completed")}</h2>
          <RequestTiles items={finished} label={t("myRequests.recentTitle", "Recently completed")} />
        </section>
      )}
    </div>
  );

  // The view controls sit at the top of the page, above the assigned requests, and govern both lists.
  const showSwitch = !narrow && (hasAssigned || state.kind === "loading" || state.kind === "error" || (state.kind === "loaded" && items.length > 0));

  // With the assigned requests above, the person's own are named, so the two lists are told apart.
  const ownHeading =
    hasAssigned && state.kind === "loaded" && items.length > 0 ? (
      <h2 className="panel-title">{t("myRequests.ownTitle", "My Requests")}</h2>
    ) : null;

  const failed =
    state.kind === "error" ? (
      <ErrorState title={t("myRequests.errorTitle", "We could not load your requests")} description={state.message} onRetry={() => void load()} />
    ) : null;

  // The panel for asking to join a team sits with the person's own requests, under their heading, when there is a list to sit in.
  const joinPanel = canJoinTeam ? <JoinTeamPanel onRaised={() => void load(true)} /> : null;
  const joinInList = narrow ? state.kind === "loaded" : state.kind === "loading" || state.kind === "error" || (state.kind === "loaded" && items.length > 0);

  return (
    // The table fills the screen and scrolls its own body; tiles are a list the page itself scrolls, with the window's scrollbar, in a
    // centred column whose top right holds the view controls, as on Organization and My Groups.
    <div className={view === "tiles" && !narrow ? `${styles.page} ${styles.tileBody}` : styles.page} data-page-scrolls={narrow || view === "tiles" || hasAssigned ? true : undefined}>
      {showSwitch && <RequestViewSwitch view={view} onChange={setView} showCompleted={showCompleted} onShowCompletedChange={setCompletedChoice} />}
      {isApprover && <ApproverRequests assignedToMe view={narrow ? "tiles" : view} onVisibleChange={setHasAssigned} />}
      {!joinInList && joinPanel}
      {state.kind === "loading" && narrow && <RequestTileLoading label={loadingLabel} />}
      {state.kind === "denied" && <PermissionDeniedState title={t("myRequests.denied.title", "Sign in to see your requests")} description={t("myRequests.denied.description", "Your requests appear here once you have signed in.")} />}
      {state.kind === "error" && narrow && failed}
      {state.kind === "loaded" && items.length === 0 && !narrow && <EmptyState title={t("myRequests.emptyTitle", "No requests yet")} description={t("myRequests.emptyDescription", "You have not raised any requests.")} />}
      {state.kind === "loaded" && narrow && (
        <>
          {ownHeading}
          {joinPanel}
          {renderTiles(completed.slice(0, RecentCompletedCount))}
        </>
      )}
      {/* Loading, a failed load and the loaded requests share one layout, so a retry does not swap the page between two. */}
      {(state.kind === "loading" || state.kind === "error" || (state.kind === "loaded" && items.length > 0)) && !narrow && (
        <div className={view === "tiles" ? styles.body : hasAssigned ? `${styles.body} ${styles.bodyBelowAssigned}` : styles.body}>
          {ownHeading}
          {joinPanel}
          {state.kind === "error" ? (
            view === "tiles" ? (
              failed
            ) : (
              <RequestHistoryTable items={[]} expandable forRequester emptyContent={failed} />
            )
          ) : state.kind === "loading" ? (
            view === "tiles" ? (
              <RequestTileLoading label={loadingLabel} />
            ) : (
              <RequestHistoryTable items={[]} expandable forRequester loadingLabel={loadingLabel} />
            )
          ) : view === "tiles" ? (
            renderTiles(showCompleted ? completed : [])
          ) : visible.length === 0 ? (
            noPending
          ) : (
            <RequestHistoryTable items={visible} expandable forRequester onCancelled={() => void load(true)} />
          )}
        </div>
      )}
    </div>
  );
}
